import { memo, useMemo } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@repo/ui/components/ui/accordion";
import type { FAQSectionProps } from "../types/components/core.type";
import SectionHeader from "#components/shared/SectionHeader.tsx";
import parse from "html-react-parser";

const FAQSection = ({
    aemContent = {
        questionAnswerPairs: [],
        heading: "",
    },
}: FAQSectionProps) => {
    const { heading, questionAnswerPairs } = aemContent;

    // Memoize the parsed FAQ list to avoid re-parsing on every render
    const parsedFaqList = useMemo(() => {
        return questionAnswerPairs.map((item) => ({
            questionText: parse(item?.questionText),
            answerText: parse(item?.answerText),
        }));
    }, [questionAnswerPairs]);

    const hasFaqList = parsedFaqList?.length > 0;

    return (
        <div
            data-testid="faq-block"
            className="container px-4 lg:px-[6.25%] mx-auto my-10 space-y-6 sm:space-y-12 sm:my-20"
        >
            <SectionHeader
                header={heading}
                className="text-[#0C2074] text-2xl !text-[28px] lg:text-3xl mb-8"
            />
            <Accordion type="single" collapsible className="w-full">
                {hasFaqList &&
                    parsedFaqList.map((item, index) => (
                        <AccordionItem
                            value={`faq-item-${index}`}
                            key={`faq-item-${index}-${item?.questionText}`}
                            className="border-b-[1px] first:border-t-[1px] border-x-0 bg-background my-0 border-[#979797]"
                        >
                            <AccordionTrigger
                                className="
                                    text-neutral-700
                                    justify-start gap-2 text-left py-3 text-[12px] leading-5 sm:py-4 sm:gap-3 sm:text-[16px] sm:leading-6 hover:no-underline [&>svg]:-order-1 [&>svg]:rotate-[-90deg] [&[data-state=open]>svg]:rotate-0 [&>p]:font-medium"
                            >
                                {item.questionText}
                            </AccordionTrigger>
                            <AccordionContent className="pb-2 p1-4 text-neutral-600 sm:pb-3 sm:pl-6">
                                {item.answerText}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
            </Accordion>
        </div>
    );
};

export default memo(FAQSection);